/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { accessAPI, downloadFile, formatFecha } from "../../shared";
import { Titulo } from "../../shared";
import { Boton } from "../../shared";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { CampoInformacion } from "../../shared";
import ActividadEnPropuesta from "./ActividadEnPropuesta";

export default function PropuestaComercial() {
  const [propuesta, setPropuesta] = useState(null);
  const [loader, setLoader] = useState(true);
  const [monedas, setMonedas] = useState();
  const [loaderDescarga, setLoaderDescarga] = useState(false);

  const toast = useRef(null);
  const { propuestaId } = useParams();
  const [searchParams] = useSearchParams();

  // Construir URL de volver basado en query params
  const getUrlVolver = () => {
    const from = searchParams.get("from");
    const auditoriaId = searchParams.get("auditoriaId");
    const procesoId = searchParams.get("procesoId");

    if (from === "auditoriasg" && auditoriaId && procesoId) {
      return `/auditoriasg/${auditoriaId}/proceso/${procesoId}`;
    }
    return "/";
  };

  // Función para descargar el PDF de la propuesta
  const descargarPDF = () => {
    setLoaderDescarga(true);
    downloadFile(
      `auditorsg/auditoria/propuesta/${propuestaId}/pdf`,
      `propuesta_comercial_${propuestaId}.pdf`,
      () => {
        setLoaderDescarga(false);
      },
      (error) => {
        setLoaderDescarga(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: error[0]?.msg || "No se pudo descargar el PDF",
            sticky: true,
          });
        }
      },
    );
  };

  useEffect(() => {
    if (propuestaId) {
      accessAPI(
        "GET",
        `auditorsg/auditoria/propuesta/${propuestaId}`,
        null,
        (data) => {
          setPropuesta(data);
        },
        (respuesta) => {
          if (toast.current) {
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail:
                respuesta[0]?.msg ||
                "Ocurrió un error al cargar la propuesta.",
              sticky: true,
            });
          }
        },
      );
    }

    accessAPI(
      "GET",
      "auditorsg/config/monedas",
      null,
      (respuesta) => {
        setMonedas(respuesta);
      },
      (respuesta) => {
        // Si falla, usar monedas por defecto
        setMonedas([
          { id: 1, nombre: "Pesos uruguayos", simbolo: "$" },
          { id: 2, nombre: "Dólares americanos", simbolo: "USD" },
        ]);
      },
    );
  }, [propuestaId]);

  useEffect(() => {
    if (propuesta && monedas) {
      setLoader(false);
    }
  }, [propuesta, monedas]);

  return (
    <>
      <Toast ref={toast} position="top-center" />
      {loader && (
        <div>
          {/* Skeleton del título */}
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
              marginTop: "2em",
            }}
          >
            <Skeleton width="18rem" height="1.5rem" />
            <Skeleton width="10rem" height="2.5rem" />
          </div>

          {/* Skeleton de detalles */}
          <Card css={{ marginBottom: "0.5em" }}>
            <Skeleton width="8rem" height="1.5rem" css={{ marginBottom: "1rem" }} />
            <div className="grid">
              {[1, 2, 3, 4].map((i) => (
                <div className="col-12 md:col-6" key={i}>
                  <Skeleton width="6rem" height="0.9rem" css={{ marginBottom: "0.3rem" }} />
                  <Skeleton width="10rem" height="1.1rem" />
                </div>
              ))}
            </div>
          </Card>

          {/* Skeleton de actividades */}
          <Card css={{ marginBottom: "0.5em" }}>
            <Skeleton width="14rem" height="1.5rem" css={{ marginBottom: "1rem" }} />
            <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {[1, 2].map((i) => (
                <div
                  key={i}
                  css={{
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                  }}
                >
                  <Skeleton width="60%" height="1.2rem" css={{ marginBottom: "0.5rem" }} />
                  <Skeleton width="40%" height="1rem" />
                </div>
              ))}
            </div>
          </Card>

          {/* Skeleton de costo total */}
          <Card>
            <Skeleton width="10rem" height="1.5rem" css={{ marginBottom: "1rem" }} />
            <Skeleton width="12rem" height="1.2rem" />
          </Card>
        </div>
      )}
      {!loader && propuesta && monedas && (
        <div>
          <Titulo
            urlVolver={getUrlVolver()}
            botones={
              <Boton callback={descargarPDF} loader={loaderDescarga}>
                <i className="pi pi-download" css={{ marginRight: "0.5em" }}></i>
                Descargar PDF
              </Boton>
            }
          >
            {propuesta.solicitud?.nombrefantasia
              ? `Propuesta comercial - ${propuesta.solicitud.nombrefantasia}`
              : "Propuesta comercial"}
          </Titulo>

          <Card css={{ marginBottom: "0.5em" }}>
            <Titulo>Detalles</Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Cliente">
                {propuesta.cliente?.razonsocial}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Solicitud">
                {propuesta.solicitud?.nombrefantasia}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de emisión">
                {formatFecha(propuesta.fechaemision)}
              </CampoInformacion>
              {propuesta.fechaaceptacion && (
                <CampoInformacion nombreCampo="Fecha de aceptación">
                  <span css={{ color: "var(--verde)" }}>
                    {formatFecha(propuesta.fechaaceptacion)}
                  </span>
                </CampoInformacion>
              )}
              {!propuesta.fechaaceptacion && (
                <CampoInformacion nombreCampo="Estado">
                  <span css={{ color: "var(--amarillo)" }}>
                    Pendiente de aceptación
                  </span>
                </CampoInformacion>
              )}
              {propuesta.notas && (
                <CampoInformacion nombreCampo="Notas">
                  {propuesta.notas.split("\n").map((parrafo, index) => (
                    <span key={index}>
                      {parrafo}
                      <br />
                    </span>
                  ))}
                </CampoInformacion>
              )}
            </div>
          </Card>

          {propuesta.precios?.actividades?.length > 0 && (
            <Card css={{ marginBottom: "0.5em" }}>
              <Titulo>Actividades incluidas</Titulo>
              {propuesta.precios.actividades.map((actividad, index) => (
                <ActividadEnPropuesta
                  key={index}
                  actividad={actividad}
                  monedas={monedas}
                  propuesta={propuesta}
                  index={index}
                />
              ))}
            </Card>
          )}

          {propuesta.precios?.totales?.length > 0 && (
            <Card css={{ marginBottom: "0.5em" }}>
              <Titulo>Costo total</Titulo>
              {propuesta.precios.totales.map((total, index) => (
                <div key={index}>
                  <strong>{`${total.moneda.simbolo} ${total.total} (${total.moneda.nombre})`}</strong>
                  {propuesta.masiva && <span> más IVA</span>}
                </div>
              ))}
            </Card>
          )}

          {propuesta.fechaaceptacion && propuesta.nombreAceptador && (
            <Card>
              <p>
                Propuesta aceptada por{" "}
                <strong>{propuesta.nombreAceptador}</strong>{" "}
                el <strong>{formatFecha(propuesta.fechaaceptacion)}</strong>.
              </p>
            </Card>
          )}
        </div>
      )}
    </>
  );
}
